import { argbFromHex, Hct, hexFromArgb } from "@material/material-color-utilities";
import { ENTITY_PALETTE_SIZE, type SchemeVariant, type ThemeConfig } from "./types.js";

/** Chroma per variant — higher = more colorful entity chips. Tone is fixed so
 *  the palette reads as one tonal family (harmonious) while the hues stay
 *  distinct (differentiable) — the opposite of a single-source tonal ramp. */
const CHROMA_BY_VARIANT: Record<SchemeVariant, number> = {
  neutral: 20,
  tonal_spot: 48,
  content: 52,
  expressive: 64,
  vibrant: 72,
};

/** Base tone for the categorical fills (mode-agnostic; the block wash softens
 *  per light/dark). Mid tone reads against both light and dark surfaces. */
const ENTITY_TONE = 52;
const orderCache = new Map<string, number[]>();

/**
 * A harmonious-but-distinct categorical palette: `count` hues spread evenly
 * around the wheel starting at the source hue, all at a fixed chroma + tone.
 * This deliberately does NOT use Material dynamic-color generation (which keeps
 * everything in the source's hue family → indistinguishable topics).
 */
export function categoricalPalette(
  sourceHex: string,
  variant: SchemeVariant,
  count: number = ENTITY_PALETTE_SIZE,
): string[] {
  const baseHue = Hct.fromInt(argbFromHex(sourceHex)).hue;
  const chroma = CHROMA_BY_VARIANT[variant];
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    const hue = (baseHue + (360 / count) * i) % 360;
    out.push(hexFromArgb(Hct.from(hue, chroma, ENTITY_TONE).toInt()));
  }
  return out;
}

/** Shift a single color's HCT chroma by `boost` (clamped ≥ 0), preserving hue +
 *  tone. Used by the vibrancy slider to lift the whole curated palette at once. */
export function applyChromaBoost(hex: string, boost: number): string {
  if (!boost) return hex;
  try {
    const c = Hct.fromInt(argbFromHex(hex));
    return hexFromArgb(Hct.from(c.hue, Math.max(0, c.chroma + boost), c.tone).toInt());
  } catch {
    return hex;
  }
}

/** The effective entity palette = curated `entityPalette` with the vibrancy
 *  boost applied, then explicit per-slot overrides on top (overrides are the
 *  user's exact pick, so they are NOT boosted). */
export function paletteFor(theme: ThemeConfig): string[] {
  return theme.entityPalette.map(
    (hex, i) => theme.paletteOverrides[i] ?? applyChromaBoost(hex, theme.chromaBoost),
  );
}

/** Categorical color for a stable slot index (cycles when index ≥ palette size). */
export function categoricalColor(theme: ThemeConfig, index: number): string {
  const palette = paletteFor(theme);
  if (palette.length === 0) return "#64748b";
  return palette[((index % palette.length) + palette.length) % palette.length]!;
}

function wrapIndex(index: number, length: number): number {
  return ((index % length) + length) % length;
}

function rgb(hex: string): [number, number, number] {
  const value = hex.replace("#", "");
  return [
    Number.parseInt(value.slice(0, 2), 16),
    Number.parseInt(value.slice(2, 4), 16),
    Number.parseInt(value.slice(4, 6), 16),
  ];
}

function colorDistance(a: string, b: string): number {
  const [ar, ag, ab] = rgb(a);
  const [br, bg, bb] = rgb(b);
  const redMean = (ar + br) / 2;
  const dr = ar - br;
  const dg = ag - bg;
  const db = ab - bb;
  return Math.sqrt((2 + redMean / 256) * dr * dr + 4 * dg * dg + (2 + (255 - redMean) / 256) * db * db);
}

function minDistanceTo(hex: string, usedHexes: readonly string[]): number {
  if (usedHexes.length === 0) return Number.POSITIVE_INFINITY;
  return Math.min(...usedHexes.map((used) => colorDistance(hex, used)));
}

function distributedOrderFor(palette: readonly string[]): number[] {
  const key = palette.map((hex) => hex.toLowerCase()).join("|");
  const cached = orderCache.get(key);
  if (cached) return cached;
  if (palette.length === 0) return [];

  const order = [0];
  const remaining = new Set(palette.slice(1).map((_, index) => index + 1));
  while (remaining.size > 0) {
    let bestIndex: number | null = null;
    let bestDistance = -1;
    for (const index of remaining) {
      const distance = minDistanceTo(palette[index]!, order.map((usedIndex) => palette[usedIndex]!));
      if (distance > bestDistance) {
        bestIndex = index;
        bestDistance = distance;
      }
    }
    order.push(bestIndex!);
    remaining.delete(bestIndex!);
  }

  orderCache.set(key, order);
  return order;
}

/** Palette slot for a topic-like ordered entity. Slots are assigned farthest
 *  first so neighboring topic indexes stay visually easy to distinguish. */
export function distributedPaletteIndex(theme: ThemeConfig, index: number): number {
  const palette = paletteFor(theme);
  if (palette.length === 0) return 0;
  return distributedOrderFor(palette)[wrapIndex(index, palette.length)]!;
}

/** Topic/default entity color in the farthest-first palette order. */
export function distributedCategoricalColor(theme: ThemeConfig, index: number): string {
  const palette = paletteFor(theme);
  if (palette.length === 0) return "#64748b";
  return palette[distributedPaletteIndex(theme, index)]!;
}

/**
 * Suggest a default color for a NEW topic/calendar: prefer unused palette slots,
 * then choose the color farthest from already-visible entity colors.
 */
export function suggestEntityColor(
  theme: ThemeConfig,
  usedHexes: readonly string[],
): { hex: string; index: number } {
  const palette = paletteFor(theme);
  if (palette.length === 0) return { hex: "#64748b", index: 0 };
  const normalizedUsed = usedHexes.map((hex) => hex.toLowerCase());
  const countUses = (hex: string) => normalizedUsed.filter((used) => used === hex.toLowerCase()).length;
  let bestIndex = distributedOrderFor(palette)[0]!;
  let bestUseCount = countUses(palette[bestIndex]!);
  let bestDistance = minDistanceTo(palette[bestIndex]!, usedHexes);
  for (const index of distributedOrderFor(palette).slice(1)) {
    const hex = palette[index]!;
    const useCount = countUses(hex);
    const distance = minDistanceTo(hex, usedHexes);
    if (useCount < bestUseCount || (useCount === bestUseCount && distance > bestDistance)) {
      bestIndex = index;
      bestUseCount = useCount;
      bestDistance = distance;
    }
  }
  return { hex: palette[bestIndex]!, index: bestIndex };
}
